import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import {
  formatDate,
  formatTime,
  converStatusToReadable,
  PickupOrderStatusInitState,
  PickupOrderStatusReadyForPickUp,
  PickupOrderStatusReadyToBeProcessed,
  PickupOrderStatusInitStateInProgress,
  PickupOrderStatusStartLoading,
  PickupOrderStatusLoadingDone,
  PickupOrderStatusBeingLoaded,
  PickupOrderStatusStartLoadingInProgress,
  PickupOrderStatusOnCall,
  setBodyStyleObject,
} from '../../util/util';
import { showNotification, confirmPopup } from '../../LanghoutBetonPortal';
import {
  PickupOrder,
  PickupSubOrder,
  Product,
  StyleObject,
} from '../../types/types';

import '../../elements/langhout-button/langhout-button';
import '../langhout-beton-update-pickup-order/langhout-beton-update-pickup-order';
import '../../elements/langhout-checkbox/langhout-checkbox';
import { LanghoutCheckbox } from '../../elements/langhout-checkbox/langhout-checkbox';
import { closeIcon } from '../../elements/icons/icons';
import { sharedStyles } from '../../elements/shared-styles';

@customElement('langhout-beton-view-pickup-order')
export class ViewPickupOrder extends LitElement {
  private readonly styleObject: StyleObject = {
    '--wrapper-width': '1000px',
  };

  static styles = [
    sharedStyles,
    css`
      :host {
        font-size: 17px;
        color: var(--text-color-main);
        height: calc(100vh - 84px);
        max-height: calc(100vh - 84px);
        overflow-y: auto;
        background-color: white;
        z-index: 100;
        padding: 10px;
        padding-left: 30px;
        padding-right: 30px;
      }

      .row {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
      }

      .centered {
        justify-content: center;
      }

      .inner-wrapper {
        max-width: var(--wrapper-width);
        flex-grow: 1;
      }

      .centered-column {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .wrapper {
        padding-bottom: 400px;
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .close-wrapper {
        padding: 5px;
      }

      .label {
        text-align: left;
        min-width: 180px;
        font-weight: bold;
        flex-grow: 1;
        max-width: calc(var(--wrapper-width) / 2);
      }

      .action-label {
        text-align: left;
        min-width: 180px;
        max-width: 180px;
        font-weight: bold;
        flex-grow: 1;
      }

      .value {
        text-align: right;
        min-width: 100px;
        flex-grow: 1;
        max-width: calc(var(--wrapper-width) / 2);
      }

      .row-inner-wrapper {
        display: flex;
        flex-direction: row;
      }

      .prod-row {
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .prod-amount {
        text-align: left;
        width: 180px;
        font-weight: bold;
      }

      .prod-name {
        text-align: left;
        min-width: 180px;
        max-width: 250px;
      }

      .amount-units {
        text-align: left;
        min-width: 100px;
        max-width: 250px;
      }

      langhout-button {
        width: 200px;
        min-width: 180px;
        margin-right: 20px;
        margin-top: 20px;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .button-row {
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .action-row {
        margin-bottom: 20px;
      }

      .sub-order-wrapper {
        padding: 10px;
        border: 2px solid var(--primary-color);
        margin-top: 15px;
        border-radius: 5px;
      }

      .add-comment-wrapper {
        margin-top: 20px;
        display: flex;
        flex-direction: row;
      }

      #comment {
        min-width: 200px;
      }

      .comment-input-wrapper {
        margin-top: 20px;
      }

      .comment-wrapper {
        padding: 10px;
        border: 2px solid var(--primary-color);
        margin-top: 15px;
        border-radius: 5px;
      }

      .comment-message {
        text-align: left;
      }

      .comment-user-name,
      .comment-time {
        font-weight: bold;
        color: var(--primary-color);
      }

      select {
        padding: 5px;
        width: calc(100% - 4px);
        height: 37.19px;
        border-radius: 5px;
        border: 2px solid black;
        font-size: 16px;
      }

      #commentButton {
        margin-right: 0px;
      }

      .comment-space {
        width: 40px;
      }

      .checkbox-text {
        text-align: left;
        width: 321px;
      }

      .pdf-viewer {
        margin-top: 20px;
        width: 100%;
        margin-left: 20px;
        margin-right: 20px;
        max-width: var(--wrapper-width);
        min-height: 980px;
      }

      .text-area-display {
        white-space: pre-line;
        text-align: left;
      }
    `,
  ];

  @property({ type: Object, reflect: true })
  public currentViewOrder: PickupOrder = <PickupOrder>{
    sub_orders: <PickupSubOrder[]>[],
  };

  @query('#comment')
  public commentInput?: HTMLInputElement;

  @query('#licensePlate')
  public licensePlate?: HTMLInputElement;

  @query('#transporter')
  public transporter?: HTMLInputElement;

  @query('#transporterDropdown')
  public transporterDropdown?: HTMLSelectElement;

  @query('#bookDirectlyCheckbox')
  public bookDirectlyCheckbox?: LanghoutCheckbox;

  @query('#returnProductsCheckbox')
  public returnProductsCheckbox?: LanghoutCheckbox;

  @property({ type: Boolean, reflect: true })
  private edit = false;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  constructor() {
    super();
    setBodyStyleObject(this.styleObject);
    document.addEventListener(
      'confirm_pickup_order_deletion',
      this.removePickupOrderCall,
    );
    document.addEventListener(
      'confirm_pickup_order_cleanup',
      this.cleanupPickupOrderCall,
    );
    document.addEventListener(
      'confirm_pickup_order_email',
      this.sendConfirmationEmailCall,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      'confirm_pickup_order_deletion',
      this.removePickupOrderCall,
    );
    document.removeEventListener(
      'confirm_pickup_order_cleanup',
      this.cleanupPickupOrderCall,
    );
    document.removeEventListener(
      'confirm_pickup_order_email',
      this.sendConfirmationEmailCall,
    );
  }

  render() {
    if (this.edit) {
      return html`
        <div class="row">
          <div class="fill"></div>
          <div
            class="close-wrapper"
            @click="${() => {
              this.edit = false;
              this.dispatchCloseEvent();
            }}"
          >
            ${closeIcon()}
          </div>
        </div>
        <langhout-beton-update-pickup-order
          .pickupOrder=${this.currentViewOrder}
          @done=${(e: CustomEvent) => {
            this.currentViewOrder = e.detail;
            this.edit = false;
            this.scrollTo(0, 0);
            this.dispatchReloadEvent();
          }}
        ></langhout-beton-update-pickup-order>
      `;
    }
    return html`
      <div class="row">
        <div class="fill"></div>
        <div
          class="close-wrapper"
          @click="${() => {
            this.dispatchCloseEvent();
          }}"
        >
          ${closeIcon()}
        </div>
      </div>
      <div class="wrapper">
        <div class="inner-wrapper">
          <div class="row centered">
            <h2>Pickup order</h2>
          </div>
          <div class="row centered display-props">
            <div class="label">Status:</div>
            <div class="value">
              ${converStatusToReadable(this.currentViewOrder.status)}
            </div>
          </div>
          <div class="row centered display-props">
            <div class="label">Laaddatum:</div>
            <div class="value">
              ${formatDate(this.currentViewOrder.pickup_date)}
            </div>
          </div>
          ${this.renderInitUser(this.currentViewOrder)}
          ${this.renderReadyForPickupTime(this.currentViewOrder)}
          ${this.renderLoadingUser(this.currentViewOrder)}
          ${this.renderBeingLoadedTime(this.currentViewOrder)}
          ${this.renderCleanupUser(this.currentViewOrder)}
          ${this.renderCleanupTime(this.currentViewOrder)}
          ${this.renderTransporter(this.currentViewOrder)}
          ${this.renderLicensePlate(this.currentViewOrder)}
          ${this.currentViewOrder.sub_orders.map(subOrder => {
            return html`
              <div class="sub-order-wrapper">
                <div class="row">
                  <h3>Order</h3>
                </div>
                <div class="row centered display-props">
                  <div class="label">Ordernummer LH:</div>
                  <div class="value">${subOrder.order_id}</div>
                </div>
                <div class="row centered display-props">
                  <div class="label">Vracht ID:</div>
                  <div class="value">${subOrder.shipping_id}</div>
                </div>
                <div class="row centered display-props">
                  <div class="label">Ordernummer klant:</div>
                  <div class="value">${subOrder.external_id}</div>
                </div>
                <div class="row centered display-props">
                  <div class="label">Klantnaam:</div>
                  <div class="value">${subOrder.customer_name}</div>
                </div>
                <div class="row centered display-props">
                  <div class="label">Plaatsnaam:</div>
                  <div class="value">${subOrder.shipping_city}</div>
                </div>
                <div class="row centered display-props">
                  <div class="label">Pakbon notitie:</div>
                  <div class="value">${subOrder.package_receipt_note}</div>
                </div>
                <div class="row centered display-props">
                  <div class="label">PDF notitie:</div>
                  <div class="value text-area-display">
                    ${subOrder.pickup_order_pdf_comment}
                  </div>
                </div>
                <div class="row">
                  <h3>Producten</h3>
                </div>
                ${subOrder.products.map(product => {
                  return html`
                    <div class="row centered display-props prod-row">
                      <div class="prod-amount">
                        ${product.amount} st.
                        ${this.getAmountText(
                          product.amount_packs,
                          product.product_type,
                        )}
                      </div>
                      <div class="amount-units">
                        ${
                          Math.round(
                            (product.amount / product.pieces_per_unit) * 1000,
                          ) / 1000
                        }
                        ${product.unit}
                      </div>
                      <div class="prod-name">${product.description}</div>
                      <div class="fill"></div>
                      <div class="prod-weight">
                        <div>
                          ${this.getWeightText(product.amount, product.weight)}
                        </div>
                      </div>
                    </div>
                  `;
                })}
                <div class="row">
                  <div>Totaal gewicht:</div>
                  <div class="fill"></div>
                  ${this.getWeightTotalText(subOrder.products)}
                </div>
                ${this.renderPickupOrderPDF(subOrder)}
              </div>
            `;
          })}
          ${this.renderComments()}
          <div class="add-comment-wrapper button-row centered">
            <div class="centered-column comment-input-wrapper">
              <input
                type="text"
                placeholder="Opmerking"
                id="comment"
                name="comment"
                @keypress=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    this.addComment();
                  }
                }}
              />
            </div>
            <div class="comment-space"></div>
            <langhout-button
              id="commentButton"
              text="Opmerking plaatsen"
              @click="${() => {
                const message = this.commentInput
                  ? this.commentInput.value
                  : '';
                if (message === '') {
                  showNotification('Opmerking kan niet leeg zijn');
                  return;
                }
                this.addComment();
              }}"
            >
            </langhout-button>
          </div>
          <div class="row centered">
            <h3>Acties</h3>
          </div>
          ${this.renderStartLoadingForm(this.currentViewOrder)}
          <div class="row centered button-row">
            ${this.renderStartLoadingButton(this.currentViewOrder)}
            ${this.renderConfirmationEmailButton(this.currentViewOrder)}
            ${this.renderOrderCompleteButton(this.currentViewOrder)}
            ${this.renderCancelStartLoading(this.currentViewOrder)}
            <langhout-button
              text="Wijzigen"
              reverse
              @click="${() => {
                this.scrollTo(0, 0);
                this.edit = true;
              }}"
            >
            </langhout-button>
            ${this.renderDetachButton(this.currentViewOrder)}
            ${this.renderRemoveButton(this.currentViewOrder)}
          </div>
          ${this.renderHistory()}
        </div>
      </div>
    `;
  }

  renderComments = () => {
    if (
      !this.currentViewOrder.comments ||
      this.currentViewOrder.comments.length === 0
    ) {
      return html``;
    }
    return html`
      <div class="row">
        <h3>Opmerkingen</h3>
      </div>
      ${this.currentViewOrder.comments.map(comment => {
        return html`
          <div class="comment-wrapper">
            <div class="row">
              <div class="comment-user-name">${comment.user_name}</div>
              <div class="fill"></div>
              <div class="comment-time">
                ${formatTime(comment.timestamp, false)}
              </div>
            </div>
            <div class="row comment-message">${comment.message}</div>
          </div>
        `;
      })}
    `;
  };

  renderPickupOrderPDF = (subOrder: PickupSubOrder) => {
    if (!subOrder.pickup_order_pdf || subOrder.pickup_order_pdf === '') {
      return html``;
    }
    return html`
      <div class="row">
        <iframe
          class="pdf-viewer"
          src="data:application/pdf;base64,${subOrder.pickup_order_pdf}#navpanes=0"
        ></iframe>
      </div>
    `;
  };

  renderHistory = () => {
    if (
      !this.currentViewOrder.history ||
      this.currentViewOrder.history.length === 0
    ) {
      return html``;
    }
    return html`
      <div class="row">
        <h3>Order geschiedenis</h3>
      </div>
      ${this.currentViewOrder.history.map(history => {
        return html`
          <div class="comment-wrapper">
            <div class="row">
              <div class="comment-user-name">${history.user_name}</div>
              <div class="fill"></div>
              <div class="comment-time">
                ${formatTime(history.timestamp, false)}
              </div>
            </div>
            <div class="row comment-message">
              ${converStatusToReadable(history.status)}
            </div>
          </div>
        `;
      })}
    `;
  };

  getWeightText = (amount: number, weight: number): string => {
    const weightTotal = amount * weight;
    return `${weightTotal.toLocaleString('nl-NL')} kg`;
  };

  getWeightTotalText = (products: Product[]): string => {
    let totalWeight = 0;
    products.forEach((p: Product) => {
      totalWeight += p.amount * p.weight;
    });
    return `${Math.ceil(totalWeight).toLocaleString('nl-NL')} kg`;
  };

  getAmountText = (amount: number, type: number): string => {
    if (type === 2) {
      return '';
    }
    if (amount === 1) {
      return `(${amount} pak)`;
    }
    return `(${amount} pakken)`;
  };

  renderBeingLoadedTime = (pickupOrder: PickupOrder) => {
    if (pickupOrder.being_loaded_time === '') {
      return html``;
    }
    const time = new Date(pickupOrder.being_loaded_time);
    if (time.getFullYear() < 2020) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Geladen om:</div>
        <div class="value">
          ${formatTime(this.currentViewOrder.being_loaded_time, false)}
        </div>
      </div>
    `;
  };

  renderReadyForPickupTime = (pickupOrder: PickupOrder) => {
    if (pickupOrder.ready_for_pickup_time === '') {
      return html``;
    }
    const time = new Date(pickupOrder.ready_for_pickup_time);
    if (time.getFullYear() < 2020) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Klaargezet om:</div>
        <div class="value">
          ${formatTime(this.currentViewOrder.ready_for_pickup_time, false)}
        </div>
      </div>
    `;
  };

  renderCleanupTime = (pickupOrder: PickupOrder) => {
    if (pickupOrder.cleanup_time === '') {
      return html``;
    }
    const time = new Date(pickupOrder.cleanup_time);
    if (time.getFullYear() < 2020) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Opgeruimd om:</div>
        <div class="value">
          ${formatTime(this.currentViewOrder.cleanup_time, false)}
        </div>
      </div>
    `;
  };

  renderInitUser = (pickupOrder: PickupOrder) => {
    if (pickupOrder.init_user === '' || !pickupOrder.init_user) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Klaargezet door:</div>
        <div class="value">${this.currentViewOrder.init_user}</div>
      </div>
    `;
  };

  renderLoadingUser = (pickupOrder: PickupOrder) => {
    if (pickupOrder.loading_user === '' || !pickupOrder.loading_user) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Geladen door:</div>
        <div class="value">${this.currentViewOrder.loading_user}</div>
      </div>
    `;
  };

  renderCleanupUser = (pickupOrder: PickupOrder) => {
    if (pickupOrder.cleanup_user === '' || !pickupOrder.cleanup_user) {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Opgeruimd door:</div>
        <div class="value">${this.currentViewOrder.cleanup_user}</div>
      </div>
    `;
  };

  renderTransporter = (pickupOrder: PickupOrder) => {
    if (pickupOrder.transporter === '') {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Transporteur:</div>
        <div class="value">${this.currentViewOrder.transporter}</div>
      </div>
    `;
  };

  renderLicensePlate = (pickupOrder: PickupOrder) => {
    if (pickupOrder.license_plate === '') {
      return html``;
    }
    return html`
      <div class="row centered display-props">
        <div class="label">Kenteken:</div>
        <div class="value">${this.currentViewOrder.license_plate}</div>
      </div>
    `;
  };

  renderDetachButton = (pickupOrder: PickupOrder) => {
    if (
      pickupOrder.sub_orders.length > 1 &&
      (pickupOrder.status === PickupOrderStatusInitState ||
        pickupOrder.status === PickupOrderStatusReadyToBeProcessed ||
        pickupOrder.status === PickupOrderStatusInitStateInProgress ||
        pickupOrder.status === PickupOrderStatusReadyForPickUp)
    ) {
      return html`
        <langhout-button
          reverse
          text="Order loskoppelen"
          @click="${async () => {
            this.disableButtons = true;
            this.scrollTo(0, 0);
            await this.detachPickupOrder(pickupOrder);
            this.disableButtons = false;
          }}"
        >
        </langhout-button>
      `;
    }
    return html``;
  };

  renderRemoveButton = (pickupOrder: PickupOrder) => {
    if (pickupOrder.status === PickupOrderStatusReadyForPickUp) {
      return html` <langhout-button
        text="Opruimen"
        removeButton
        @click="${() => {
          confirmPopup({
            confirm_event: 'confirm_pickup_order_cleanup',
            title: 'Opruimen bevestigen',
            description:
              'Weet u zeker dat u de order wilt laten opruimen? Deze actie kan niet ongedaan gemaakt worden!',
            with_checkbox: false,
            checkbox_text: '',
          });
        }}"
      >
      </langhout-button>`;
    }
    if (
      pickupOrder.status === PickupOrderStatusOnCall ||
      pickupOrder.status === PickupOrderStatusInitState ||
      pickupOrder.status === PickupOrderStatusReadyToBeProcessed ||
      pickupOrder.status === PickupOrderStatusInitStateInProgress
    ) {
      return html`
        <langhout-button
          text="Verwijderen"
          removeButton
          @click="${() => {
            confirmPopup({
              confirm_event: 'confirm_pickup_order_deletion',
              title: 'Verwijderen bevestigen',
              description:
                'Weet u zeker dat u de order wilt verwijderen? Deze actie kan niet ongedaan gemaakt worden!',
              with_checkbox: false,
              checkbox_text: '',
            });
          }}"
        >
        </langhout-button>
      `;
    }
    return html``;
  };

  renderStartLoadingForm = (pickupOrder: PickupOrder) => {
    if (
      pickupOrder.status === PickupOrderStatusOnCall ||
      pickupOrder.status === PickupOrderStatusInitState ||
      pickupOrder.status === PickupOrderStatusReadyToBeProcessed ||
      pickupOrder.status === PickupOrderStatusInitStateInProgress ||
      pickupOrder.status === PickupOrderStatusReadyForPickUp
    ) {
      return html`
        <div class="row action-row centered">
          <div class="action-label">Kenteken:</div>
          <div class="prod-name">
            <input
              value=${this.currentViewOrder.license_plate}
              type="text"
              id="licensePlate"
              name="licensePlate"
            />
          </div>
        </div>
        ${this.renderTransporterInput(pickupOrder)}

        <div class="row action-row centered">
          <langhout-checkbox
            checked
            id="bookDirectlyCheckbox"
          ></langhout-checkbox>
          <div class="checkbox-text">Order direct afboeken</div>
        </div>
        <div class="row action-row centered">
          <langhout-checkbox
            .checked="${pickupOrder.has_return_products}"
            id="returnProductsCheckbox"
          ></langhout-checkbox>
          <div class="checkbox-text">Heeft retour producten</div>
        </div>
      `;
    }
    return html``;
  };

  renderTransporterInput = (pickupOrder: PickupOrder) => {
    if (pickupOrder.account_transporters.length === 0) {
      return html`
        <div class="row action-row centered">
          <div class="action-label">Transporteur:</div>
          <div class="prod-name">
            <input
              value=${this.currentViewOrder.transporter}
              type="text"
              id="transporter"
              name="transporter"
            />
          </div>
        </div>
      `;
    }
    return html`
      <div class="row action-row centered">
        <div class="action-label">Transporteur:</div>
        <div class="prod-name">
          <select id="transporterDropdown" name="transporter">
            ${pickupOrder.account_transporters.map((transporter, i) => {
              return html`
                <option .selected=${i == 0} value="${transporter.id}">
                  ${transporter.name}
                </option>
              `;
            })}
          </select>
        </div>
      </div>
    `;
  };

  renderCancelStartLoading = (pickupOrder: PickupOrder) => {
    if (
      pickupOrder.status === PickupOrderStatusStartLoading ||
      pickupOrder.status === PickupOrderStatusStartLoadingInProgress
    ) {
      return html`
        <langhout-button
          text="Start laden annuleren"
          removeButton
          @click="${async () => {
            this.disableButtons = true;
            await this.cancelStartLoading(pickupOrder);
            this.disableButtons = false;
          }}"
        >
        </langhout-button>
      `;
    }
    return html``;
  };

  renderOrderCompleteButton = (pickupOrder: PickupOrder) => {
    if (pickupOrder.status === PickupOrderStatusBeingLoaded) {
      return html`
        <langhout-button
          text="Afboeken"
          @click="${async () => {
            this.disableButtons = true;
            const body = {
              status: PickupOrderStatusLoadingDone,
            };
            await this.updateStatus(pickupOrder, body, 'Order afgerond');
            this.disableButtons = false;
          }}"
        ></langhout-button>
      `;
    }
    return html``;
  };

  renderStartLoadingButton = (pickupOrder: PickupOrder) => {
    if (
      pickupOrder.status === PickupOrderStatusOnCall ||
      pickupOrder.status === PickupOrderStatusInitState ||
      pickupOrder.status === PickupOrderStatusReadyToBeProcessed ||
      pickupOrder.status === PickupOrderStatusInitStateInProgress ||
      pickupOrder.status === PickupOrderStatusReadyForPickUp
    ) {
      return html`
        <langhout-button
          text="Start laden"
          @click="${async () => {
            this.disableButtons = true;
            await this.startLoading(pickupOrder);
            this.disableButtons = false;
          }}"
        ></langhout-button>
      `;
    }
    return html``;
  };

  renderConfirmationEmailButton = (pickupOrder: PickupOrder) => {
    return html`
      <langhout-button
        text=${
          pickupOrder.confirmation_email_sent
            ? 'Nogmaals bevestigen'
            : 'Bevestigen'
        }
        reverse
        @click="${async () => {
          confirmPopup({
            confirm_event: 'confirm_pickup_order_email',
            title: 'Email versturen bevestigen',
            description:
              'Weet u zeker dat u de bevestigings-email wilt versturen?',
            with_checkbox: false,
            checkbox_text: '',
          });
        }}"
      ></langhout-button>
    `;
  };

  sendConfirmationEmail = async (pickupOrder: PickupOrder) => {
    const path = `/pickup-orders/${pickupOrder.id}/confirmation-email`;
    const result = await authenticatedAPICall(path, 'POST', new Headers());
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      showNotification('Pickup bevestiging verstuurd');
    }
  };

  startLoading = async (pickupOrder: PickupOrder) => {
    if (!this.licensePlate) {
      showNotification('something unexpected went wrong, please refresh');
      return;
    }
    if (this.licensePlate.value === '') {
      showNotification('Please fill in the license plate');
      return;
    }
    let transporterValue = '';
    let transporterID = '';
    if (pickupOrder.account_transporters.length > 0) {
      if (!this.transporterDropdown) {
        showNotification('Please select a transporter');
        return;
      }
      transporterID = this.transporterDropdown.value;
      for (let i = 0; i < pickupOrder.account_transporters.length; i++) {
        if (pickupOrder.account_transporters[i].id === transporterID) {
          transporterValue = pickupOrder.account_transporters[i].name;
        }
      }
    } else {
      if (!this.transporter || this.transporter.value === '') {
        showNotification('Please fill in the transporter');
        return;
      } else {
        transporterValue = this.transporter.value;
      }
    }
    let bookDirectly = false;
    if (this.bookDirectlyCheckbox) {
      bookDirectly = this.bookDirectlyCheckbox.checked;
    }
    let hasReturnProducts = false;
    if (this.returnProductsCheckbox) {
      hasReturnProducts = this.returnProductsCheckbox.checked;
    }

    const body = {
      license_plate: this.licensePlate.value,
      transporter: transporterValue,
      book_directly: bookDirectly,
      transporter_id: transporterID,
      has_return_products: hasReturnProducts,
    };
    await this.startLoadingCall(pickupOrder, body);
  };

  startLoadingCall = async (
    pickupOrder: PickupOrder,
    statusPayload: Record<string, unknown>,
  ) => {
    const path = `/pickuporders/${pickupOrder.id}/start-loading`;
    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify(statusPayload),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      showNotification('Order start laden');
      this.dispatchReloadEvent();
      this.scrollTo(0, 0);
      this.dispatchCloseEvent();
    }
  };

  addComment = () => {
    const message = this.commentInput ? this.commentInput.value : '';
    if (message === '') {
      showNotification('Opmerking kan niet leeg zijn');
      return;
    }
    this.addCommentCall(this.currentViewOrder, message);
  };

  addCommentCall = async (pickupOrder: PickupOrder, message: string) => {
    const commentPayload = {
      message: message,
      type: 'portal',
    };
    const path = `/pickuporders/${pickupOrder.id}/comment`;
    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify(commentPayload),
    );

    if (isSuccessfulAPIResponse(result) && result.body.success) {
      showNotification('Opmerking toegevoegd');
      if (this.commentInput) {
        this.commentInput.value = '';
      }
      this.dispatchReloadEvent();
    }
  };

  updateStatus = async (
    pickupOrder: PickupOrder,
    statusPayload: Record<string, unknown>,
    message: string,
  ) => {
    const path = `/pickuporders/${pickupOrder.id}/status`;
    const result = await authenticatedAPICall(
      path,
      'PUT',
      new Headers(),
      JSON.stringify(statusPayload),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      showNotification(message);
      this.dispatchReloadEvent();
      this.scrollTo(0, 0);
      this.dispatchCloseEvent();
    }
  };

  cancelStartLoading = async (pickupOrder: PickupOrder) => {
    const path = `/pickuporders/${pickupOrder.id}/cancel-start-loading`;
    const result = await authenticatedAPICall(path, 'PUT', new Headers());
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      showNotification('Start laden succesvol geannuleerd');
      this.dispatchReloadEvent();
      this.scrollTo(0, 0);
    }
  };

  detachPickupOrder = async (pickupOrder: PickupOrder) => {
    const path = `/pickuporders/detach`;
    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify({
        id: pickupOrder.id,
      }),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      this.dispatchCloseEvent();
    }
  };

  removePickupOrderCall = async () => {
    this.scrollTo(0, 0);
    const result = await authenticatedAPICall(
      `/pickuporders/${this.currentViewOrder.id}`,
      'DELETE',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      this.dispatchCloseEvent();
    }
  };

  cleanupPickupOrderCall = async () => {
    this.scrollTo(0, 0);
    const result = await authenticatedAPICall(
      `/pickuporders/${this.currentViewOrder.id}/cleanup`,
      'POST',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      this.dispatchCloseEvent();
    }
  };

  sendConfirmationEmailCall = async () => {
    this.disableButtons = true;
    await this.sendConfirmationEmail(this.currentViewOrder);
    this.disableButtons = false;
  };

  dispatchReloadEvent = () => {
    const event = new CustomEvent('reload', {
      detail: {},
    });
    this.dispatchEvent(event);
  };

  dispatchCloseEvent = () => {
    const event = new CustomEvent('close', {
      detail: {},
    });
    this.dispatchEvent(event);
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-view-pickup-order': ViewPickupOrder;
  }
}
